import axios from "axios";
import { useEffect, useState } from "react";

/* ------------------------------------------------------------------ */
/* constants that drive the per-section UIs                           */
const oneToFive = [1, 2, 3, 4, 5];

/* ------------------------------------------------------------------ */
export default function PhenotypingMaintenance({
  onBack,
  onSelect,
  selected_plant_id,
}) {
  /* state that used to live in MaintenanceSelector ------------------- */
  const [selectedSex, setSelectedSex] = useState("");
  const [selectedGerm, setSelectedGerm] = useState("");
  const [selectedNutrients, setSelectedNutrients] = useState([]);
  const [selectedLeafForm, setSelectedLeafForm] = useState([]);
  const [selectedArchitecture, setSelectedArchitecture] = useState(null);
  const [selectedFlowerArch, setSelectedFlowerArch] = useState([]);
  const [selectedFlowerDensity, setSelectedFlowerDensity] = useState("");
  const [selectedInternodeSpacing, setSelectedInternodeSpacing] = useState("");
  const [selectedFlowerColour, setSelectedFlowerColour] = useState([]);
  const [selectedTrichomeExpr, setSelectedTrichomeExpr] = useState("");
  const [selectedAromas, setSelectedAromas] = useState({});
  const [aromaType, setAromaType] = useState("");
  const [leafFormType, setLeafFormType] = useState("");
  const [flowerArchType, setFlowerArchType] = useState("");
  const [flowerColourType, setFlowerColourType] = useState("");
  const [infectionType, setInfectionType] = useState("");
  const [selectedInfection, setSelectedInfection] = useState([]);
  const [plantCount, setPlantCount] = useState(0);
  const [nutrientElements, setNutrientElements] = useState([]);
  const [plantArchitecture, setPlantArchitecture] = useState([]);

  // Check if we have multiple plants (from a plot)
  useEffect(() => {
    if (Array.isArray(selected_plant_id)) {
      setPlantCount(selected_plant_id.length);
      console.log(
        `Found ${selected_plant_id.length} plants for phenotyping maintenance`
      );
    }
  }, [selected_plant_id]);

  /* local state that tracks which dropdowns are open ----------------- */
  const [openSubs, setOpenSubs] = useState(new Set());
  const toggle = (key) =>
    setOpenSubs((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  /* helper: tiny text next to the header to show current selection ---- */
  const summary = {
    Sex: selectedSex,
    "Germination Speed": selectedGerm,
    "Nutrient Imbalances": selectedNutrients.length
      ? `${selectedNutrients.length} selected`
      : "",
    "Leaf Formation": selectedLeafForm.length
      ? `${selectedLeafForm.length} selected`
      : "",
    "Plant Architecture": plantArchitecture.find(arch => arch.id === selectedArchitecture)?.name || "",
    "Flower Architecture": selectedFlowerArch.length
      ? `${selectedFlowerArch.length} selected`
      : "",
    "Flower Density": selectedFlowerDensity,
    "Internode Spacing": selectedInternodeSpacing,
    "Flower Colour": selectedFlowerColour.length
      ? `${selectedFlowerColour.length} selected`
      : "",
    "Trichome Expression": selectedTrichomeExpr,
    Aroma: Object.keys(selectedAromas).length
      ? `${Object.keys(selectedAromas).length} selected`
      : "",
    Infection: selectedInfection.length
      ? `${selectedInfection.length} selected`
      : "",
  };

  const org_id = localStorage.getItem("organization");

  useEffect(() => {
    const endpoints = {
      aroma_type: ["aroma_type", setAromaType],
      leaf_formation: ["leaf_formation", setLeafFormType],
      flower_architecture: ["flower_architecture", setFlowerArchType],
      flower_colour: ["flower_colour", setFlowerColourType],
      infection_type: ["infection_type", setInfectionType],
      nutrient_element: ["nutrient_element", setNutrientElements],
      plant_architecture: ["plant_architecture", setPlantArchitecture],
    };

    Object.entries(endpoints).forEach(([endpoint, [path, setter]]) => {
      axios
        .get(`/orgs/${org_id}/lookups/${path}?locale=en`, {
          withCredentials: true,
        })
        .then((response) => setter(response.data))
        .catch((error) => console.error(`Error fetching ${path}:`, error));
    });
  }, [org_id]);

  const handleUpload = async () => {
    console.log("Selected plant ID:", selected_plant_id);
    try {
      // Create the phenotyping data
      const phenotypingData = {
        aromas: Object.entries(selectedAromas).map(([id, intensity]) => ({
          aroma_type_id: Number(id),
          intensity: Number(intensity),
        })),
        flower_architecture_ids: selectedFlowerArch,
        flower_colour_ids: selectedFlowerColour,
        flower_density: selectedFlowerDensity
          ? parseInt(selectedFlowerDensity)
          : null,
        germination_speed: selectedGerm || null,
        infection_type_ids: selectedInfection,
        internode_spacing: selectedInternodeSpacing
          ? parseInt(selectedInternodeSpacing)
          : null,
        leaf_formation_ids: selectedLeafForm,
        nutrient_imbalances: selectedNutrients.map((ni) => ({
          element_id: ni.element_id, // Make sure this is the ID of the nutrient
          imbalance_type: ni.imbalance_type, // This should be 'Deficiency' or 'Toxicity'
        })),
        plant_architecture_ids: selectedArchitecture ? [selectedArchitecture] : [],
        sex: selectedSex || null,
        trichome_expression: selectedTrichomeExpr
          ? parseInt(selectedTrichomeExpr)
          : null,
        aroma_intensities: Object.entries(selectedAromas).map(
          ([id, intensity]) => ({
            aroma_type_id: Number(id),
            intensity: Number(intensity),
          })
        ),
      };

      // Create array of plant IDs
      const plantIds = Array.isArray(selected_plant_id)
        ? selected_plant_id
        : [selected_plant_id];

      // Send a single request with the array of plant IDs
      const requestData = {
        ...phenotypingData,
        plant_id: plantIds,
      };

      await axios.post(`/orgs/${org_id}/maintenance/phenotyping`, requestData, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      });

      alert("Upload complete");
      onBack && onBack(); // Return to previous screen after successful upload
    } catch (err) {
      console.error("Upload failed:", err);
      if (err.response?.status === 400) {
                alert('Upload failed - Check if all required fields are filled in correctly');
            } else if (err.response?.status === 401 || err.response?.status === 403) {
                alert("Upload failed - You are no longer logged in or you don't have access to this resource");
            } else if (err.response?.status === 500) {
                alert("Upload failed - Something went wrong in the service, please try again later");
            } else {
                alert(`Upload failed with error: ${err.response}`);
            }
    }
  };

  function DropdownCheckbox({
    label,
    items,
    selected,
    setSelected,
    openSubs,
    toggle,
  }) {
    const validItems = Array.isArray(items) ? items : [];

    return (
      <div className="checkbox-group">
        {validItems.map((item) => (
          <label key={item.id}>
            <input
              type="checkbox"
              className="maintenanceCheckbox"
              checked={selected.includes(item.id)}
              onChange={(e) =>
                setSelected((s) =>
                  e.target.checked
                    ? [...s, item.id]
                    : s.filter((i) => i !== item.id)
                )
              }
            />
            {item.name}
          </label>
        ))}
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  return (
    <>
      {/* header & back -------------------------------------------------- */}
      <div className="maintenanceHeader">
        <h3>Phenotyping</h3>
        <button className="uploadMaintenanceBtn" onClick={handleUpload}>
          Upload
        </button>
      </div>

      <div className="maintenaceGrid maintenanceList">
        <div className="dropdownBlock">
        <div className="purpleBtn extraGridBtn blueBtn" onClick={onBack}>
          Back
        </div>
        </div>

        {plantCount > 1 && (
          <div className="plotMaintenanceNotice">
            Applying phenotyping maintenance to all plants in plot ({plantCount}{" "}
            plants)
          </div>
        )}

        {/* ---------- Sex ------------------------------------------------ */}
        <div className="dropdownBlock">
          <div className="purpleBtn extraGridBtn" onClick={() => toggle("Sex")}>
            Sex {summary.Sex && ` - ${summary.Sex}`}{" "}
            {openSubs.has("Sex") ? "▲" : "▼"}
          </div>
          {openSubs.has("Sex") && (
            <div className="dropdownInner">
              <div className="radio-group">
                {["Male", "Female", "Hermaphrodite"].map((s) => (
                  <label key={s}>
                    <input
                      type="radio"
                      name="sex"
                      value={s}
                      checked={selectedSex === s}
                      onChange={(e) => setSelectedSex(e.target.value)}
                    />
                    {s}
                  </label>
                ))}
                <button
                  type="button"
                  className="clear-selection"
                  onClick={() => setSelectedSex("")}
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ---------- Germination Speed ---------------------------------- */}
        <div className="dropdownBlock">
          <div
            className="purpleBtn extraGridBtn"
            onClick={() => toggle("Germination Speed")}
          >
            Germination Speed
            {summary["Germination Speed"] &&
              ` - ${summary["Germination Speed"]}`}
            {openSubs.has("Germination Speed") ? " ▲" : " ▼"}
          </div>
          {openSubs.has("Germination Speed") && (
            <div className="dropdownInner">
              <div className="radio-group">
                {["24h", "48h", "72h"].map((t) => (
                  <label key={t}>
                    <input
                      type="radio"
                      name="germ"
                      value={t}
                      checked={selectedGerm === t}
                      onChange={(e) => setSelectedGerm(e.target.value)}
                    />
                    {t}
                  </label>
                ))}
                <button
                  type="button"
                  className="clear-selection"
                  onClick={() => setSelectedGerm("")}
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="dropdownBlock">
          <div
            className="purpleBtn extraGridBtn"
            onClick={() => toggle("Nutrient Imbalances")}
          >
            Nutrient Imbalances
            {summary["Nutrient Imbalances"] &&
              ` - ${summary["Nutrient Imbalances"]}`}
            {openSubs.has("Nutrient Imbalances") ? " ▲" : " ▼"}
          </div>
          {openSubs.has("Nutrient Imbalances") && (
            <div className="dropdownInner">
              <div className="maintenanceTable">
                <table>
                  <thead>
                    <tr>
                      <th>Nutrient</th>
                      <th>Deficiency</th>
                      <th>Toxicity</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nutrientElements.map((nutrient) => (
                      <tr key={nutrient.id}>
                        <td>
                          {nutrient.name} ({nutrient.code})
                        </td>
                        {["Deficiency", "Toxicity"].map((type) => (
                          <td key={type}>
                            <input
                              type="radio"
                              className="tableRadio"
                              name={nutrient.id}
                              value={type}
                              checked={selectedNutrients.some(
                                (i) =>
                                  i.element_id === nutrient.id &&
                                  i.imbalance_type === type
                              )}
                              onChange={() => {
                                // Update selected nutrients with nutrient id and type
                                setSelectedNutrients((prev) => {
                                  // Remove existing entries with the same nutrient id and type
                                  const filtered = prev.filter(
                                    (i) =>
                                      i.element_id !== nutrient.id ||
                                      i.imbalance_type !== type
                                  );

                                  // Add new entry with the selected nutrient and type
                                  return [
                                    ...filtered,
                                    {
                                      element_id: nutrient.id,
                                      imbalance_type: type,
                                    },
                                  ];
                                });
                              }}
                            />
                          </td>
                        ))}
                        <td>
                          <button
                            className="delete-btn"
                            onClick={() =>
                              setSelectedNutrients((prev) =>
                                prev.filter((i) => i.element_id !== nutrient.id)
                              )
                            }
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* ---------- Leaf Formation ------------------------------------- */}
        <div className="dropdownBlock">
          <div
            className="purpleBtn extraGridBtn"
            onClick={() => toggle("Leaf Formation")}
          >
            Leaf Formation
            {summary["Leaf Formation"] && ` - ${summary["Leaf Formation"]}`}
            {openSubs.has("Leaf Formation") ? " ▲" : " ▼"}
          </div>
          {openSubs.has("Leaf Formation") && (
            <div className="dropdownInner">
              <DropdownCheckbox
                label="Leaf Formation"
                items={leafFormType}
                selected={selectedLeafForm}
                setSelected={setSelectedLeafForm}
                openSubs={openSubs}
                toggle={toggle}
              />
              <button
                type="button"
                className="clear-selection"
                onClick={() => setSelectedLeafForm([])}
              >
                Clear
              </button>
            </div>
          )}
        </div>

        {/* ---------- Plant Architecture ---------------------------------- */}
          <div className="dropdownBlock">
            <div
              className="purpleBtn extraGridBtn"
              onClick={() => toggle("Plant Architecture")}
            >
              Plant Architecture
              {summary["Plant Architecture"] &&
                ` - ${summary["Plant Architecture"]}`}
              {openSubs.has("Plant Architecture") ? " ▲" : " ▼"}
            </div>
            {openSubs.has("Plant Architecture") && (
              <div className="dropdownInner">
                <div className="radio-group">
            {plantArchitecture.map((arch) => (
              <label key={arch.id}>
                <input
                  type="radio"
                  name="plant_architecture"
                  value={arch.name}
                  checked={selectedArchitecture === arch.id}
                  onChange={(e) => setSelectedArchitecture(arch.id)}
                />
                {arch.name}
              </label>
            ))}
            <button
              type="button"
              className="clear-selection"
              onClick={() => setSelectedArchitecture("")}
            >
              Clear
            </button>
                </div>
              </div>
            )}
          </div>

          {/* ---------- Flower Architecture -------------------------------- */}
        <div className="dropdownBlock">
          <div
            className="purpleBtn extraGridBtn"
            onClick={() => toggle("Flower Architecture")}
          >
            Flower Architecture
            {summary["Flower Architecture"] &&
              ` - ${summary["Flower Architecture"]}`}
            {openSubs.has("Flower Architecture") ? " ▲" : " ▼"}
          </div>
          {openSubs.has("Flower Architecture") && (
            <div className="dropdownInner">
              <DropdownCheckbox
                label="Flower Architecture"
                items={flowerArchType}
                selected={selectedFlowerArch}
                setSelected={setSelectedFlowerArch}
                openSubs={openSubs}
                toggle={toggle}
              />
              <button
                type="button"
                className="clear-selection"
                onClick={() => setSelectedFlowerArch([])}
              >
                Clear
              </button>
            </div>
          )}
        </div>

        {/* ---------- Flower Density (1-5) -------------------------------- */}
        <OneToFiveDropdown
          label="Flower Density"
          openSubs={openSubs}
          toggle={toggle}
          value={selectedFlowerDensity}
          setValue={setSelectedFlowerDensity}
          summary={summary["Flower Density"]}
        />

        {/* ---------- Internode Spacing (1-5) ----------------------------- */}
        <OneToFiveDropdown
          label="Internode Spacing"
          openSubs={openSubs}
          toggle={toggle}
          value={selectedInternodeSpacing}
          setValue={setSelectedInternodeSpacing}
          summary={summary["Internode Spacing"]}
        />

        {/* ---------- Trichome Expression (1-5) --------------------------- */}
        <OneToFiveDropdown
          label="Trichome Expression"
          openSubs={openSubs}
          toggle={toggle}
          value={selectedTrichomeExpr}
          setValue={setSelectedTrichomeExpr}
          summary={summary["Trichome Expression"]}
        />

        {/* ---------- Flower Colour -------------------------------------- */}
        <div className="dropdownBlock">
          <div
            className="purpleBtn extraGridBtn"
            onClick={() => toggle("Flower Colour")}
          >
            Flower Colour
            {summary["Flower Colour"] && ` - ${summary["Flower Colour"]}`}
            {openSubs.has("Flower Colour") ? " ▲" : " ▼"}
          </div>
          {openSubs.has("Flower Colour") && (
            <div className="dropdownInner">
              <DropdownCheckbox
                label="Flower Colour"
                items={flowerColourType}
                selected={selectedFlowerColour}
                setSelected={setSelectedFlowerColour}
                openSubs={openSubs}
                toggle={toggle}
              />
              <button
                type="button"
                className="clear-selection"
                onClick={() => setSelectedFlowerColour([])}
              >
                Clear
              </button>
            </div>
          )}
        </div>

        {/* ---------- Aroma (radio grid) ---------------------------------- */}
        <div className="dropdownBlock">
          <div
            className="purpleBtn extraGridBtn"
            onClick={() => toggle("Aroma")}
          >
            Aroma
            {summary.Aroma && ` - ${summary.Aroma}`}
            {openSubs.has("Aroma") ? " ▲" : " ▼"}
          </div>
          {openSubs.has("Aroma") && (
            <div className="dropdownInner">
              <div className="maintenanceTable">
                <table>
                  <thead>
                    <tr>
                      <th>Aroma</th>
                      <th colSpan="5">Intensity</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aromaType &&
                      aromaType.map((a) => (
                        <tr key={a.id}>
                          <td>{a.name}</td>
                          {oneToFive.map((score) => (
                            <td key={score}>
                              <input
                                type="radio"
                                className="tableRadio tableRadioAroma"
                                name={a.code}
                                value={score}
                                checked={selectedAromas?.[a.id] === score}
                                onChange={() => {
                                  const active = Object.keys(
                                    selectedAromas
                                  ).filter(
                                    (k) => k !== a.id && selectedAromas[k]
                                  ).length;
                                  if (active < 3 || selectedAromas[a.id]) {
                                    setSelectedAromas((prev) => ({
                                      ...prev,
                                      [a.id]: score,
                                    }));
                                  } else {
                                    alert("Maximum 3 aromas can be selected");
                                  }
                                }}
                              />
                              {score}
                            </td>
                          ))}
                          <td>
                            <button
                              className="delete-btn"
                              onClick={() =>
                                setSelectedAromas((prev) => {
                                  const n = { ...prev };
                                  delete n[a.id];
                                  return n;
                                })
                              }
                            >
                              Clear
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                <button
                  type="button"
                  className="clear-selection"
                  onClick={() => setSelectedAromas({})}
                >
                  Clear All
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ---------- Infection ------------------------------------------ */}
        <div className="dropdownBlock">
          <div
            className="purpleBtn extraGridBtn"
            onClick={() => toggle("Infection")}
          >
            Infection
            {summary.Infection && ` - ${summary.Infection}`}
            {openSubs.has("Infection") ? " ▲" : " ▼"}
          </div>
          {openSubs.has("Infection") && (
            <div className="dropdownInner">
              <DropdownCheckbox
                label="Infection"
                items={infectionType}
                selected={selectedInfection}
                setSelected={setSelectedInfection}
                openSubs={openSubs}
                toggle={toggle}
              />
              <button
                type="button"
                className="clear-selection"
                onClick={() => setSelectedInfection([])}
              >
                Clear
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ------- Done button (returns all selections to parent) ----------- */}
      {/* <button
        className="purpleBtn extraGridBtn greenBtn"
        onClick={() =>
          onSelect({
            Sex:                 selectedSex,
            GerminationSpeed:    selectedGerm,
            NutrientImbalances:  selectedNutrients,
            LeafFormation:       selectedLeafForm,
            PlantArchitecture:   selectedArchitecture,
            FlowerArchitecture:  selectedFlowerArch,
            FlowerDensity:       selectedFlowerDensity,
            InternodeSpacing:    selectedInternodeSpacing,
            FlowerColour:        selectedFlowerColour,
            TrichomeExpression:  selectedTrichomeExpr,
            Aroma:               selectedAroma,
            Infection:           selectedInfection,
          })
        }
      >
        Done
      </button> */}
    </>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* little reusable block for the 1-to-5 scale sections ----------------- */
function OneToFiveDropdown({
  label,
  openSubs,
  toggle,
  value,
  setValue,
  summary,
}) {
  // Helper for scale labels
  const scaleLabels = {
    "Flower Density": ["Very Compact", "Very Loose"],
    "Internode Spacing": ["Tight", "Wide"],
    "Trichome Expression": ["No Trichomes", "Fully Covered"],
    Aroma: ["Weak", "Strong"],
  };

  return (
    <div className="dropdownBlock">
      <div className="purpleBtn extraGridBtn" onClick={() => toggle(label)}>
        {label}
        {summary && ` - ${summary}`}
        {openSubs.has(label) ? " ▲" : " ▼"}
      </div>
      {openSubs.has(label) && (
        <div className="dropdownInner">
          <div className="scaleLabels">
            <p>{scaleLabels[label][0]}</p>
            <p>{scaleLabels[label][1]}</p>
          </div>
          <div className="radio-group radioBtnScale">
            {oneToFive.map((n) => (
              <label key={n}>
                <input
                  type="radio"
                  name={label}
                  value={n}
                  checked={value === String(n)}
                  onChange={(e) => setValue(e.target.value)}
                />
                {n}
              </label>
            ))}
            <button
              type="button"
              className="clear-selection"
              onClick={() => setValue("")}
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
